import React from 'react';
import { motion } from 'framer-motion';
import { FaTrophy, FaDollarSign, FaClock, FaExclamationTriangle, FaChartLine, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';


function Step1() {
  return (
    <div className="h-full overflow-auto p-4 xl:p-6"> {/* 👈 xl: برای مانیتور بزرگ */}
      <div className="max-w-7xl mx-auto h-full flex flex-col justify-center">
        <div className="space-y-3 xl:space-y-5"> {/* 👈 فضای بیشتر فقط برای مانیتور */}
          {/* Header */}
          <motion.div 
            className="text-center mb-2 xl:mb-4"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 0.8 
            }}
          >
            <div className="inline-flex items-center justify-center w-12 h-12 xl:w-16 xl:h-16 rounded-2xl mb-2 xl:mb-3"
              style={{
                backgroundColor: "#374151",
                boxShadow: "0 4px 20px rgba(55, 65, 81, 0.5)",
              }}
            >
              <FaTrophy className="text-white text-2xl xl:text-3xl" />
            </div>
            <motion.h1 
              className="text-2xl xl:text-3xl font-bold text-white mb-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              چالش اول: انتخاب کاپیتان‌ها
            </motion.h1>
            <motion.p 
              className="text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              نبرد برای کسب امتیاز و رهبری تیم
            </motion.p>
          </motion.div>

          {/* Main Cards Container - 2 columns on laptop, 1 column on big monitor */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-1 gap-3">
            {/* Main Info */}
            <motion.div 
              className="p-3 xl:p-5 rounded-2xl backdrop-blur-xl border"
              style={{
                backgroundColor: "rgba(31, 41, 55, 0.6)",
                borderColor: "rgba(253, 13, 14, 0.3)",
              }}
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: 0.2 
              }}
            >
              <div className="grid grid-cols-3 gap-3 xl:gap-6 mb-2 xl:mb-4">
                <motion.div 
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 xl:w-10 xl:h-10 rounded-xl mb-1 xl:mb-2"
                    style={{
                      backgroundColor: "#374151",
                    }}
                  >
                    <FaDollarSign className="text-white text-sm xl:text-lg" />
                  </div>
                  <div className="text-gray-400 text-xs mb-1">سرمایه اولیه</div>
                  <div className="text-red-400 text-lg xl:text-xl font-bold">$5,000</div>
                </motion.div>

                <motion.div 
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 xl:w-10 xl:h-10 rounded-xl mb-1 xl:mb-2"
                    style={{
                      backgroundColor: "#374151",
                    }}
                  >
                    <FaClock className="text-white text-sm xl:text-lg" />
                  </div>
                  <div className="text-gray-400 text-xs mb-1">مدت زمان</div>
                  <div className="text-red-400 text-lg xl:text-xl font-bold">1 ساعت</div>
                </motion.div>

                <motion.div 
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 xl:w-10 xl:h-10 rounded-xl mb-1 xl:mb-2"
                    style={{
                      backgroundColor: "#374151",
                    }}
                  >
                    <FaCheckCircle className="text-white text-sm xl:text-lg" />
                  </div>
                  <div className="text-gray-400 text-xs mb-1">حداقل پوزیشن</div>
                  <div className="text-red-400 text-lg xl:text-xl font-bold">5</div>
                </motion.div>
              </div>

              <div className="border-t pt-2 xl:pt-3"
                style={{ borderColor: "rgba(253, 13, 14, 0.2)" }}
              >
                <p className="text-gray-300 text-xs xl:text-sm text-center leading-relaxed">
                  پس از 1 ساعت تمام پوزیشن‌ها به صورت خودکار بسته می‌شوند. برای شرکت معتبر در چالش باید حداقل 5 پوزیشن باز کنید.
                </p>
              </div>
            </motion.div>

            {/* Risk Management */}
            <motion.div 
              className="p-3 xl:p-5 rounded-2xl backdrop-blur-xl border"
              style={{
                backgroundColor: "rgba(31, 41, 55, 0.6)",
                borderColor: "rgba(253, 13, 14, 0.3)",
              }}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: 0.4 
              }}
            >
              <div className="flex items-center gap-2 mb-2 xl:mb-3">
                <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-xl flex items-center justify-center"
                  style={{
                    backgroundColor: "#374151",
                  }}
                >
                  <FaExclamationTriangle className="text-white text-xs xl:text-sm" />
                </div>
                <h2 className="text-white font-bold text-base xl:text-lg">مدیریت ریسک</h2>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 xl:gap-3">
  

                <motion.div 
                  className="p-2 xl:p-3 rounded-xl text-center"
                  style={{
                    backgroundColor: "rgba(253, 13, 14, 0.05)",
                    borderRight: "3px solid #fd0d0e",
                  }}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px rgba(0,0,0,0.3)" }}
                >
                  <div className="text-white font-bold text-xs xl:text-sm mb-1">ریسک روزانه</div>
                  <div className="text-red-400 text-lg xl:text-xl font-bold">$1,000</div>
                  <div className="text-gray-400 text-xs mt-1">حداکثر ضرر در کل روز</div>
                </motion.div>

                <motion.div 
                  className="p-2 xl:p-3 rounded-xl"
                  style={{
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                  }}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.5 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start gap-2">
                    <FaInfoCircle className="text-red-400 flex-shrink-0 mt-0.5 text-xs" />
                    <div className="text-right">
                      <div className="text-red-400 font-bold text-xs xl:text-sm mb-1">Drawdown</div>
                      <p className="text-gray-300 text-xs leading-relaxed">
                        معاملات شما تا پایان چالش غیرفعال می‌شود
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Goal & Scoring - Full Width */}
          <motion.div 
            className="p-3 xl:p-5 rounded-2xl backdrop-blur-xl border"
            style={{
              backgroundColor: "rgba(31, 41, 55, 0.6)",
              borderColor: "rgba(253, 13, 14, 0.3)",
            }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 15,
              delay: 0.6 
            }}
            whileHover={{ scale: 1.01 }}
          >
            <div className="flex items-center gap-2 mb-2 xl:mb-3">
              <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: "#374151",
                }}
              >
                <FaChartLine className="text-white text-xs xl:text-sm" />
              </div>
              <h2 className="text-white font-bold text-base xl:text-lg">هدف چالش و امتیازدهی</h2>
            </div>

            <div className="space-y-2 xl:space-y-3">
              <p className="text-gray-300 text-xs xl:text-sm leading-relaxed text-right">
                هدف اصلی این چالش <span className="text-purple-400 font-bold">تعیین کاپیتان‌های تیم‌ها</span> است. 
                در پایان این مرحله، بر اساس بیشترین سود و کمترین ضرر، یک لیدربورد تشکیل می‌شود و امتیازات به شرکت‌کنندگان تعلق می‌گیرد.
              </p>

              <div className="p-2 xl:p-3 rounded-xl"
                style={{
                  backgroundColor: "rgba(139, 92, 246, 0.1)",
                  borderRight: "3px solid #8b5cf6",
                }}
              >
                <div className="text-purple-400 font-bold text-xs xl:text-sm mb-1">نحوه امتیازدهی:</div>
                <p className="text-gray-300 text-xs leading-relaxed text-right">
                  رتبه اول 60 امتیاز، رتبه دوم 55 امتیاز، رتبه سوم 50 امتیاز و به همین ترتیب با کاهش 5 امتیازی ادامه می‌یابد. 
                  این امتیازات در تعیین نهایی کاپیتان‌ها تأثیرگذار خواهند بود.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Final Note */}
          <motion.div 
            className="p-3 xl:p-4 rounded-2xl backdrop-blur-xl border text-center"
            style={{
              backgroundColor: "rgba(253, 13, 14, 0.1)",
              borderColor: "rgba(253, 13, 14, 0.3)",
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 0.8,
              duration: 0.6,
              ease: "easeOut"
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 8px 30px rgba(0,0,0,0.4)"
            }}
          >
            <p className="text-gray-300 text-xs xl:text-sm">
              <span className="text-red-400 font-bold">یادآوری:</span> مدیریت زمان و ریسک در این چالش بسیار حیاتی است.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Step1;